// ============================================================================
// ContactCard.tsx — карточка контакта в чате ассистента (SSoT/SRP).
// Показывает профиль и ЕДИНЫЕ каналы связи; действия — ContactActions.
// Источник данных — lib/contacts/contactService (UI не вызывает RPC).
// ============================================================================

import { CheckCircle2, CircleAlert, Mail, MapPin, Phone, PlugZap, UserRound } from 'lucide-react';
import { UserAvatar } from '@/components/ui/UserAvatar';
import type { ContactAction, ContactChannel, ContactDetails } from '@/lib/contacts/types';
import { ContactActions } from './ContactActions';

interface ContactCardProps {
  details: ContactDetails;
  busyKind?: string;
  onAction: (action: ContactAction) => void;
  onRetry?: () => void;
  /** В профиле контакта кнопка «Открыть профиль» уже не нужна. */
  showProfileAction?: boolean;
}

function ChannelIcon({ channel }: { channel: ContactChannel }) {
  if (channel.source === 'adapter') return <PlugZap className="h-3.5 w-3.5 text-primary" />;
  if (channel.kind === 'internal') return <UserRound className="h-3.5 w-3.5" />;
  return <PlugZap className="h-3.5 w-3.5 text-muted-foreground" />;
}

function sourceLabel(channel: ContactChannel): string {
  return channel.source === 'adapter' ? 'подключение' : 'платформа';
}

export function ContactCard({ details, busyKind, onAction, onRetry, showProfileAction = true }: ContactCardProps) {
  return (
    <div className="glass-panel rounded-xl p-3 space-y-3">
      <div className="flex items-start gap-2.5">
        <UserAvatar uid={details.id} fileId={details.avatarUrl} name={details.name} size={44} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-[14px] font-semibold leading-tight">{details.name}</div>
          <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[10px] text-muted-foreground">
            {details.login && <span className="truncate">@{details.login}</span>}
            {details.technical && (
              <span className="rounded-full border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-amber-700 dark:text-amber-300">
                внешний контакт
              </span>
            )}
          </div>
        </div>
      </div>

      {(details.phone || details.email || details.city) && (
        <div className="space-y-1 border-t border-border/60 pt-2 text-[11px]">
          {details.phone && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Phone className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{details.phone}</span>
            </div>
          )}
          {details.email && (
            <div className="flex items-center gap-1.5 min-w-0">
              <Mail className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{details.email}</span>
            </div>
          )}
          {details.city && (
            <div className="flex items-center gap-1.5 min-w-0">
              <MapPin className="h-3 w-3 shrink-0 text-muted-foreground" />
              <span className="truncate">{details.city}</span>
            </div>
          )}
        </div>
      )}

      <div className="space-y-1 border-t border-border/60 pt-2">
        <div className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Каналы связи</div>
        {details.channels.map((channel) => (
          <div key={`${channel.kind}:${channel.value}:${channel.source}`} className="flex items-center gap-1.5 min-w-0 text-[11px]">
            <ChannelIcon channel={channel} />
            <span className="shrink-0 text-muted-foreground">{channel.label}</span>
            {channel.value && (
              <span className="min-w-0 truncate" title={channel.externalName || channel.value}>
                {channel.externalName || channel.value}
              </span>
            )}
            <span className="ml-auto shrink-0 rounded bg-muted px-1 text-[9px] text-muted-foreground">{sourceLabel(channel)}</span>
            {channel.verified && <CheckCircle2 className="h-3 w-3 shrink-0 text-emerald-600 dark:text-emerald-400" />}
          </div>
        ))}
      </div>

      {details.channelsError && (
        <div className="flex items-start gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-[10px] text-amber-800 dark:text-amber-300">
          <CircleAlert className="mt-0.5 h-3 w-3 shrink-0" />
          <div className="min-w-0 flex-1 space-y-1">
            <div className="break-words">{details.channelsError}</div>
            {onRetry && (
              <button type="button" className="underline" onClick={onRetry}>Повторить</button>
            )}
          </div>
        </div>
      )}

      <ContactActions details={details} busyKind={busyKind} onAction={onAction} compact showProfile={showProfileAction} />
    </div>
  );
}
